import { useEffect, useRef, useState } from "react"
import * as hooks from "lib/hooks"
import * as result from "lib/result"
import * as util from "lib/util"
import { useParams } from "react-router-dom"
import {
	Box,
	Typography,
	Button,
	Paper,
	CircularProgress,
	Container,
	Link,
} from "@mui/material"
import CloudDownloadIcon from "@mui/icons-material/CloudDownload"

export function DownloadPage() {
	const { theme } = hooks.UseTheme()
	const mccApi = hooks.UseMccApi()
	const snack = hooks.UseSnackBar()
	const { id } = useParams()

	const [loading, setLoading] = useState(false)
	const [downloadResult, setDownloadResult] = useState<result.Result<boolean, Error> | null>(null)

	const hasDownloadedOnce = useRef(false)

	if (isNil(id)) {
		log.Fatal("id is required for this page")
	}

	const fileName = fmt.Sprintf("%s%s", id, util.DownloadExtension)

	const download = async () => {
		try {
			setLoading(true)
			const blob = await mccApi.DownloadFile(fileName)
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = url
			link.setAttribute("download", fileName)
			document.body.appendChild(link)
			link.click()
			setTimeout(() => {
				window.URL.revokeObjectURL(url)
				document.body.removeChild(link)
			}, 100)
			const r = result.Ok(true)
			setDownloadResult(r)

			snack.open(result.Ok("File downloaded successfully"))
		} catch (err) {
			const r = result.Err<boolean, Error>(err instanceof Error ? err : new Error("Unknown error"))

			setDownloadResult(r)

			snack.open(result.Err(`Download failed: ${r.UnwrapErr().message}`))
		} finally {
			setLoading(false)
		}
	}


	useEffect(() => {
		if (hasDownloadedOnce.current) return
		hasDownloadedOnce.current = true

		download()
	}, [])

	return (
		<Container maxWidth="sm">
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "80vh",
					py: 4,
				}}
			>
				<Paper
					elevation={2}
					sx={{
						width: "100%",
						p: 4,
						borderRadius: 2,
						textAlign: "center",
					}}
				>
					<Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 500 }}>
						Your File Download
					</Typography>

					{loading ? (
						<Box
							sx={{
								py: 3,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
							}}
						>
							<CircularProgress size={40} thickness={4} />
							<Typography variant="body1" sx={{ mt: 3 }}>
								Preparing your download...
							</Typography>
						</Box>
					) : (
						<Box sx={{ py: 2 }}>
							{downloadResult ? (
								downloadResult.Match({
									Ok: () => (
										<Typography variant="body1" sx={{ mb: 2 }}>
											Your file should download automatically.
										</Typography>
									),
									Err: () => (
										<Typography variant="body1" color="error" sx={{ mb: 2 }}>
											The download link has expired. Please request a new one
											through the MCC system. If the issue persists, contact IT
											support for assistance.
										</Typography>
									),
								})
							) : (
								<Typography variant="body1" sx={{ mb: 2 }}>
									Ready to download.
								</Typography>
							)}

							<Button
								variant="contained"
								color="primary"
								size="large"
								startIcon={<CloudDownloadIcon />}
								onClick={download}
								sx={{ mt: 1, px: 3, py: 1, borderRadius: 1, textTransform: "none" }}
								disabled={loading || (downloadResult?.IsErr?.() ?? false)}
							>
								Download Again
							</Button>
						</Box>
					)}
				</Paper>
			</Box>
		</Container>
	)
}
